import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Lecon, Chapitre, Video } from '../../types';
import ChapitresLeconsApiService from '../../services/chapitresLeconsApi';
import VideosApiService from '../../services/videosApi';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import type { FieldChangeEvent } from '../../types/forms';
import { logger } from '../../lib/logger';

interface LeconFormProps {
  lecon?: Lecon | null;
  defaultChapitreId?: string;
  returnPath?: string;
  saving?: boolean;
  onSubmit: (data: Omit<Lecon, 'id'>) => void;
  onCancel: () => void;
  onChange?: (data: Omit<Lecon, 'id'>) => void;
  /** Callback appelé quand un champ perd le focus (pour auto-save) */
  onFieldBlur?: (field: string, value: unknown, allFormData: Omit<Lecon, 'id'>) => void;
  /** Chapitres proposés : fournis avec `videos`, le formulaire ne charge plus les collections entières. */
  chapitres?: Pick<Chapitre, 'id' | 'titre'>[];
  /** Vidéos proposées (voir `chapitres`). */
  videos?: Video[];
}

const LeconForm: React.FC<LeconFormProps> = ({ lecon, defaultChapitreId, saving, onSubmit, onCancel, onChange, onFieldBlur, chapitres: chapitresFournis, videos: videosFournies }) => {
  const { environment } = useEnvironment();


  const [formData, setFormData] = useState<Omit<Lecon, 'id'>>({
    chapitreId: defaultChapitreId || '',
    dateCreation: new Date().toISOString().split('T')[0],
    dateModification: new Date().toISOString().split('T')[0],
    titre: '',
    description: '',
    ordre: 1,
    niveauDifficulte: 'moyen',
    motsCles: [],
    tempsEstimeMinutes: 60,
    contenuLatex: '',
    estActif: true,
    thumbnailLatex: '',
    videoId: '',
    statut: 'draft' // Par défaut en brouillon
  });

  const [newMotCle, setNewMotCle] = useState('');
  const [chapitresCharges, setChapitres] = useState<Pick<Chapitre, 'id' | 'titre'>[]>([]);
  const [videosChargees, setVideos] = useState<Video[]>([]);
  const chapitres = chapitresFournis ?? chapitresCharges;
  const videos = videosFournies ?? videosChargees;
  const listesFournies = !!chapitresFournis && !!videosFournies;

  useEffect(() => {
    if (listesFournies) return;
    const loadData = async () => {
      try {
        const [chapitresData, videosData] = await Promise.all([
          ChapitresLeconsApiService.getChapitres(environment),
          VideosApiService.getVideos(environment)
        ]);
        setChapitres(chapitresData);
        setVideos(videosData);
      } catch (error) {
        logger.error('Erreur lors du chargement des données:', error);
      }
    };

    loadData();
  }, [environment, listesFournies]);

  useEffect(() => {
    if (lecon) {
      // Données de base de la leçon
      const baseData: Omit<Lecon, 'id'> = {
        chapitreId: lecon.chapitreId,
        dateCreation: lecon.dateCreation,
        dateModification: new Date().toISOString().split('T')[0],
        titre: lecon.titre,
        description: lecon.description,
        ordre: lecon.ordre,
        niveauDifficulte: lecon.niveauDifficulte,
        motsCles: lecon.motsCles,
        tempsEstimeMinutes: lecon.tempsEstimeMinutes,
        contenuLatex: lecon.contenuLatex,
        estActif: lecon.estActif,
        thumbnailLatex: lecon.thumbnailLatex,
        videoId: lecon.videoId,
        statut: lecon.statut || 'draft',
        datePublication: lecon.datePublication,
        historiqueStatut: lecon.historiqueStatut
      };


      setFormData(baseData);
    }
  }, [lecon]);

  // Appeler onChange à chaque modification du formData pour la prévisualisation en temps réel
  useEffect(() => {
    if (onChange) {
      onChange(formData);
    }
  }, [formData, onChange]);

  const handleChange = (field: keyof Omit<Lecon, 'id'>) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.type === 'checkbox'
      ? (event.target as HTMLInputElement).checked
      : event.target.value;

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSelectChange = (field: keyof Omit<Lecon, 'id'>) => (
    event: FieldChangeEvent
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleAddMotCle = () => {
    if (newMotCle.trim() && !formData.motsCles.includes(newMotCle.trim())) {
      setFormData(prev => ({
        ...prev,
        motsCles: [...prev.motsCles, newMotCle.trim()]
      }));
      setNewMotCle('');
    }
  };

  const handleDeleteMotCle = (motCle: string) => {
    setFormData(prev => ({
      ...prev,
      motsCles: prev.motsCles.filter(m => m !== motCle)
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {/* Informations de base */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Informations de base</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Titre"
                  value={formData.titre}
                  onChange={handleChange('titre')}
                  onBlur={() => onFieldBlur?.('titre', formData.titre, formData)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Chapitre</InputLabel>
                  <Select
                    value={formData.chapitreId}
                    onChange={handleSelectChange('chapitreId')}
                    onBlur={() => onFieldBlur?.('chapitreId', formData.chapitreId, formData)}
                  >
                    {chapitres.map((chapitre) => (
                      <MenuItem key={chapitre.id} value={chapitre.id}>
                        {chapitre.titre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={handleChange('description')}
                  onBlur={() => onFieldBlur?.('description', formData.description, formData)}
                  multiline
                  rows={3}
                  required
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  label="Ordre"
                  type="number"
                  value={formData.ordre}
                  onChange={handleChange('ordre')}
                  onBlur={() => onFieldBlur?.('ordre', formData.ordre, formData)}
                  required
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Niveau</InputLabel>
                  <Select
                    value={formData.niveauDifficulte}
                    onChange={handleSelectChange('niveauDifficulte')}
                    onBlur={() => onFieldBlur?.('niveauDifficulte', formData.niveauDifficulte, formData)}
                    required
                  >
                    <MenuItem value="facile">Facile</MenuItem>
                    <MenuItem value="moyen">Moyen</MenuItem>
                    <MenuItem value="difficile">Difficile</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  label="Durée (minutes)"
                  type="number"
                  value={formData.tempsEstimeMinutes}
                  onChange={handleChange('tempsEstimeMinutes')}
                  onBlur={() => onFieldBlur?.('tempsEstimeMinutes', formData.tempsEstimeMinutes, formData)}
                  required
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Vidéo</InputLabel>
                  <Select
                    value={formData.videoId}
                    onChange={handleSelectChange('videoId')}
                    onBlur={() => onFieldBlur?.('videoId', formData.videoId, formData)}
                  >
                    <MenuItem value="">
                      <em>Aucune vidéo</em>
                    </MenuItem>
                    {videos
                      .filter(video => video.isActive)
                      .map((video) => (
                        <MenuItem key={video.id} value={video.id}>
                          {video.title}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Mots-clés */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Mots-clés</Typography>
            <Box display="flex" gap={1} mb={2}>
              <TextField
                label="Nouveau mot-clé"
                value={newMotCle}
                onChange={(e) => setNewMotCle(e.target.value)}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddMotCle();
                  }
                }}
                size="small"
              />
              <Button onClick={handleAddMotCle} variant="outlined">
                Ajouter
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {formData.motsCles.map((motCle, index) => (
                <Chip
                  key={index}
                  label={motCle}
                  onDelete={() => handleDeleteMotCle(motCle)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Contenu LaTeX */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Contenu LaTeX</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                    Contenu du cours
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="Contenu LaTeX"
                  value={formData.contenuLatex}
                  onChange={handleChange('contenuLatex')}
                  onBlur={() => onFieldBlur?.('contenuLatex', formData.contenuLatex, formData)}
                  multiline
                  rows={10}
                  placeholder="Contenu LaTeX de la leçon (produit hors plateforme)..."
                  sx={{ fontFamily: 'monospace' }}
                  helperText="Document LaTeX complet du cours avec explications, formules et exemples"
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Thumbnail LaTeX */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Thumbnail LaTeX</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                    Miniature de la leçon
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="Thumbnail LaTeX"
                  value={formData.thumbnailLatex}
                  onChange={handleChange('thumbnailLatex')}
                  onBlur={() => onFieldBlur?.('thumbnailLatex', formData.thumbnailLatex, formData)}
                  multiline
                  rows={5}
                  placeholder="Code LaTeX de la miniature (produit hors plateforme)..."
                  sx={{ fontFamily: 'monospace' }}
                  helperText="Miniature attractive pour représenter la leçon (200x200 points)"
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Statut */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Statut et activation</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.statut || 'draft'}
                    onChange={handleSelectChange('statut')}
                    required
                  >
                    <MenuItem value="draft">🔸 Brouillon</MenuItem>
                    <MenuItem value="published">✅ Publié</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.estActif}
                      onChange={(e) => setFormData(prev => ({ ...prev, estActif: e.target.checked }))}
                    />
                  }
                  label="Leçon active"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onCancel} disabled={saving}>
          Annuler
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {saving ? 'Enregistrement...' : (lecon ? 'Mettre à jour' : 'Créer')}
        </Button>
      </Box>
    </Box>
  );
};

export default LeconForm;